import React, { useState, useEffect } from 'react';
import { Save, AlertCircle } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';
import toast from 'react-hot-toast';

export default function AdvancedLayoutSettings() {
  const { settings, loading, updateSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    watermark_enabled: false,
    watermark_left: 0,
    watermark_top: 0,
    name_size: 16,
    subname_size: 14,
    invoice_header_gap: 10,
    invoice_body_gap: 10,
    report_header_gap: 10,
    report_body_gap: 10,
    report_body_font_size: 12,
    report_footer_gap: 10,
    report_left_gap: 10,
    prescription_header_gap: 10,
    prescription_body_gap: 10,
    prescription_header_gradient: '',
    prescription_header_font_color: '#ffffff',
  });

  useEffect(() => {
    if (settings) {
      setFormData({
        watermark_enabled: settings.watermark_enabled || false,
        watermark_left: settings.watermark_left || 0,
        watermark_top: settings.watermark_top || 0,
        name_size: settings.name_size || 16,
        subname_size: settings.subname_size || 14,
        invoice_header_gap: settings.invoice_header_gap || 10,
        invoice_body_gap: settings.invoice_body_gap || 10,
        report_header_gap: settings.report_header_gap || 10,
        report_body_gap: settings.report_body_gap || 10,
        report_body_font_size: settings.report_body_font_size || 12,
        report_footer_gap: settings.report_footer_gap || 10,
        report_left_gap: settings.report_left_gap || 10,
        prescription_header_gap: settings.prescription_header_gap || 10,
        prescription_body_gap: settings.prescription_body_gap || 10,
        prescription_header_gradient: settings.prescription_header_gradient || '',
        prescription_header_font_color: settings.prescription_header_font_color || '#ffffff',
      });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const result = await updateSettings(formData);

    if (result.success) {
      toast.success('Advanced layout settings saved successfully');
    } else {
      toast.error(result.error || 'Failed to save settings');
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Watermark Settings */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Watermark Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Watermark Enabled
            </label>
            <select
              value={formData.watermark_enabled ? 'yes' : 'no'}
              onChange={(e) => setFormData({ ...formData, watermark_enabled: e.target.value === 'yes' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Watermark Left Margin (px)
            </label>
            <input
              type="number"
              value={formData.watermark_left}
              onChange={(e) => setFormData({ ...formData, watermark_left: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Watermark Top Margin (px)
            </label>
            <input
              type="number"
              value={formData.watermark_top}
              onChange={(e) => setFormData({ ...formData, watermark_top: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Font Sizes */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Font Sizes</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name Size (px)
            </label>
            <input
              type="number"
              min="10"
              max="32"
              value={formData.name_size}
              onChange={(e) => setFormData({ ...formData, name_size: parseInt(e.target.value) || 16 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sub-name Size (px)
            </label>
            <input
              type="number"
              min="8"
              max="24"
              value={formData.subname_size}
              onChange={(e) => setFormData({ ...formData, subname_size: parseInt(e.target.value) || 14 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Invoice Layout */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Layout Gaps</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Header Gap (px)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.invoice_header_gap}
              onChange={(e) => setFormData({ ...formData, invoice_header_gap: parseInt(e.target.value) || 10 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Body Gap (px)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.invoice_body_gap}
              onChange={(e) => setFormData({ ...formData, invoice_body_gap: parseInt(e.target.value) || 10 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Report Layout */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Report Layout Gaps</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Header Gap (px)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.report_header_gap}
              onChange={(e) => setFormData({ ...formData, report_header_gap: parseInt(e.target.value) || 10 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Body Gap (px)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.report_body_gap}
              onChange={(e) => setFormData({ ...formData, report_body_gap: parseInt(e.target.value) || 10 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Body Font Size (px)
            </label>
            <input
              type="number"
              min="8"
              max="24"
              value={formData.report_body_font_size}
              onChange={(e) => setFormData({ ...formData, report_body_font_size: parseInt(e.target.value) || 12 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Footer Gap (px)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.report_footer_gap}
              onChange={(e) => setFormData({ ...formData, report_footer_gap: parseInt(e.target.value) || 10 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Left Gap (px)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.report_left_gap}
              onChange={(e) => setFormData({ ...formData, report_left_gap: parseInt(e.target.value) || 10 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Prescription Layout */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Prescription Layout</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Header Gap (px)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.prescription_header_gap}
              onChange={(e) => setFormData({ ...formData, prescription_header_gap: parseInt(e.target.value) || 10 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Body Gap (px)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.prescription_body_gap}
              onChange={(e) => setFormData({ ...formData, prescription_body_gap: parseInt(e.target.value) || 10 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Header Font Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={formData.prescription_header_font_color}
                onChange={(e) => setFormData({ ...formData, prescription_header_font_color: e.target.value })}
                className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.prescription_header_font_color}
                onChange={(e) => setFormData({ ...formData, prescription_header_font_color: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="#ffffff"
              />
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Header Gradient (CSS)
            </label>
            <textarea
              value={formData.prescription_header_gradient}
              onChange={(e) => setFormData({ ...formData, prescription_header_gradient: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
              placeholder="linear-gradient(to right, #3b82f6, #14b8a6)"
            />
            <p className="mt-1 text-xs text-gray-500">
              CSS gradient for prescription header background
            </p>
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
        <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Advanced Settings</p>
          <p>These settings control spacing, fonts, and layout for all printed documents. Changes will apply to all future prints. Test your settings after saving to ensure proper layout.</p>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-6 border-t border-gray-200">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>
    </form>
  );
}
